/**
 * Evenements — agenda des événements de l'église, alimenté par l'API
 * (avec fallback mock si backend indisponible).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { colors, fonts, goldGradient } from "../styles/tokens";
import Card from "../components/Card";
import Badge from "../components/Badge";
import Button from "../components/Button";
import EvenementForm from "../components/EvenementForm";
import QueteForm from "../components/QueteForm";
import AssignBenevolesModal from "../components/AssignBenevolesModal";
import useAuth from "../hooks/useAuth";
import useToast from "../hooks/useToast";
import useMembers from "../hooks/useMembers";
import { evenementsApi, quetesApi, benevolesApi } from "../services/api";
import { MOCK_EVENTS } from "../services/mockData";

const TYPE_LABELS = {
  culte:      "Culte",
  reunion:    "Réunion",
  retraite:   "Retraite",
  bapteme:    "Baptême",
  formation:  "Formation",
  conference: "Conférence",
  mission:    "Mission",
  autre:      "Autre",
};

const TYPE_VARIANT = {
  culte:      "gold",
  reunion:    "info",
  retraite:   "success",
  bapteme:    "info",
  formation:  "success",
  conference: "warning",
  mission:    "gold",
  autre:      "default",
};

export default function Evenements() {
  const { isAdmin, usingMock: authMock } = useAuth();
  const { eglises } = useMembers();
  const toast = useToast();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);
  const [filter, setFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Quêtes : map evenement_id → quete (pour afficher le statut sur chaque culte)
  const [quetesByEvent, setQuetesByEvent] = useState({});
  const [queteFormOpen, setQueteFormOpen] = useState(false);
  const [quetePrefillEvent, setQuetePrefillEvent] = useState(null);

  // Bénévoles : map evenement_id → assignations (pour badge X/Y)
  const [benevolesByEvent, setBenevolesByEvent] = useState({});
  const [assignBenevolesEvent, setAssignBenevolesEvent] = useState(null);

  /** Charge les événements depuis l'API (fallback mock si réseau down). */
  const load = useCallback(async () => {
    setLoading(true);

    if (authMock) {
      setEvents(MOCK_EVENTS);
      setUsingMock(true);
      setLoading(false);
      return;
    }

    try {
      const { data } = await evenementsApi.list();
      setEvents(data || []);
      setUsingMock(false);

      // Charge les quêtes pour annoter les cultes (best-effort)
      if (isAdmin) {
        try {
          const { data: qList } = await quetesApi.list();
          const map = {};
          (qList.items || []).forEach((q) => {
            if (q.evenement_id) map[q.evenement_id] = q;
          });
          setQuetesByEvent(map);
        } catch {
          setQuetesByEvent({});
        }

        // Charge les assignations bénévoles (résumé par culte)
        try {
          const { data: planList } = await benevolesApi.listPlanning();
          const bMap = {};
          (planList || []).forEach((p) => {
            if (!p.evenement_id) return;
            if (!bMap[p.evenement_id]) bMap[p.evenement_id] = { total: 0, confirmes: 0, en_attente: 0, refuses: 0 };
            bMap[p.evenement_id].total += 1;
            bMap[p.evenement_id][p.statut === "confirme" ? "confirmes" : p.statut === "refuse" ? "refuses" : "en_attente"] += 1;
          });
          setBenevolesByEvent(bMap);
        } catch {
          setBenevolesByEvent({});
        }
      }
    } catch (err) {
      const isNetwork = !err.response || err.code === "ERR_NETWORK";
      if (isNetwork) {
        setEvents(MOCK_EVENTS);
        setUsingMock(true);
      } else {
        toast.error(err.response?.data?.detail || "Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  }, [authMock, toast, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  /** Soumet la création / édition d'un événement. */
  const handleSubmit = async (data) => {
    if (usingMock) {
      const newEvt = {
        ...data,
        id: Math.max(0, ...events.map((e) => e.id || 0)) + 1,
      };
      setEvents((prev) =>
        editing ? prev.map((e) => (e.id === editing.id ? { ...e, ...data } : e)) : [...prev, newEvt]
      );
      toast.success(editing ? "Événement mis à jour" : `« ${data.titre} » créé`);
      setFormOpen(false);
      setEditing(null);
      return { ok: true };
    }

    try {
      if (editing) {
        await evenementsApi.update(editing.id, data);
      } else {
        await evenementsApi.create(data);
      }
      toast.success(editing ? "Événement mis à jour" : `« ${data.titre} » créé`);
      setFormOpen(false);
      setEditing(null);
      await load();
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de l'enregistrement";
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const handleDelete = async (evt) => {
    if (!window.confirm(`Supprimer l'événement « ${evt.titre} » ?`)) return;
    if (usingMock) {
      setEvents((prev) => prev.filter((e) => e.id !== evt.id));
      toast.success("Événement supprimé");
      return;
    }
    try {
      await evenementsApi.remove(evt.id);
      setEvents((prev) => prev.filter((e) => e.id !== evt.id));
      toast.success("Événement supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de suppression");
    }
  };

  // Filtrage par type
  const types = useMemo(() => {
    const set = new Set(events.map((e) => e.type).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [events]);

  const filtered = useMemo(() => {
    const list = filter === "all" ? events : events.filter((e) => e.type === filter);
    return [...list].sort((a, b) => {
      const da = new Date(a.date_debut || a.date || 0).getTime();
      const db = new Date(b.date_debut || b.date || 0).getTime();
      return da - db;
    });
  }, [events, filter]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("fr-FR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  };
  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={{ fontFamily: fonts.body, color: colors.text }}>
      <header style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ fontFamily: fonts.heading, fontSize: "36px", fontWeight: 700, marginBottom: "4px" }}>
            Événements
          </h1>
          <p style={{ color: colors.textMuted, fontSize: "14px" }}>
            {filtered.length} événement{filtered.length > 1 ? "s" : ""} planifié{filtered.length > 1 ? "s" : ""}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
            + Nouvel événement
          </Button>
        )}
      </header>

      {/* Filtres types */}
      {types.length > 1 && (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
          {types.map((t) => (
            <Button
              key={t}
              variant={filter === t ? "primary" : "ghost"}
              size="sm"
              onClick={() => setFilter(t)}
            >
              {t === "all" ? "Tous" : (TYPE_LABELS[t] || t)}
            </Button>
          ))}
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div style={{ color: colors.textMuted }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <Card style={{ textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📅</div>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>Aucun événement</h3>
          <p style={{ color: colors.textMuted, marginTop: "8px" }}>
            {isAdmin ? "Créez votre premier événement." : "Aucun événement n'est planifié pour le moment."}
          </p>
          {isAdmin && (
            <div style={{ marginTop: "16px" }}>
              <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
                + Nouvel événement
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "16px" }}>
          {filtered.map((evt) => {
            const dateIso = evt.date_debut || evt.date;
            const eglise = eglises.find((e) => e.id === evt.eglise_id);
            return (
              <Card key={evt.id} hover>
                <div style={{ display: "flex", gap: "16px" }}>
                  <DateBlock iso={dateIso} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", fontWeight: 600, marginBottom: "6px" }}>
                      {evt.titre}
                    </h3>
                    <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "8px" }}>
                      {fmtDate(dateIso)}
                      {fmtTime(dateIso) && ` · ${fmtTime(dateIso)}`}
                      {evt.date_fin && fmtTime(evt.date_fin) && ` → ${fmtTime(evt.date_fin)}`}
                    </div>
                    {evt.lieu && (
                      <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "8px" }}>
                        📍 {evt.lieu}
                      </div>
                    )}
                    {evt.description && (
                      <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "12px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {evt.description}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: "6px", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                      <Badge variant={TYPE_VARIANT[evt.type] || "default"}>
                        {TYPE_LABELS[evt.type] || evt.type}
                      </Badge>
                      {eglise && (
                        <span style={{ fontSize: "11px", color: colors.textMuted }}>
                          ⛪ {eglise.nom}
                        </span>
                      )}
                    </div>
                    {/* Section quête (cultes uniquement, admin) */}
                    {isAdmin && evt.type === "culte" && (
                      <QueteSection
                        evt={evt}
                        quete={quetesByEvent[evt.id]}
                        onCreate={() => { setQuetePrefillEvent(evt); setQueteFormOpen(true); }}
                      />
                    )}

                    {/* Section bénévoles (cultes uniquement, admin) */}
                    {isAdmin && evt.type === "culte" && (
                      <BenevolesSection
                        summary={benevolesByEvent[evt.id]}
                        onManage={() => setAssignBenevolesEvent(evt)}
                      />
                    )}

                    {isAdmin && (
                      <div style={{ display: "flex", gap: "6px", marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${colors.border}` }}>
                        <Button size="sm" variant="ghost" onClick={() => { setEditing(evt); setFormOpen(true); }}>
                          Modifier
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => handleDelete(evt)}>
                          Supprimer
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <EvenementForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSubmit={handleSubmit}
        eglises={eglises}
        initial={editing}
      />

      <AssignBenevolesModal
        open={!!assignBenevolesEvent}
        onClose={() => setAssignBenevolesEvent(null)}
        evenement={assignBenevolesEvent}
        onChange={load}
      />

      <QueteForm
        open={queteFormOpen}
        onClose={() => { setQueteFormOpen(false); setQuetePrefillEvent(null); }}
        onSubmit={async (payload, { total }) => {
          try {
            await quetesApi.create(payload);
            toast.success(`✓ Quête enregistrée — Total : ${total.toLocaleString("fr-FR")} ${payload.devise}`);
            setQueteFormOpen(false);
            setQuetePrefillEvent(null);
            await load();
            return { ok: true };
          } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = typeof detail === "string" ? detail
              : Array.isArray(detail) && detail[0]?.msg ? detail[0].msg.replace(/^Value error,\s*/, "")
              : "Erreur lors de l'enregistrement";
            toast.error(msg);
            return { ok: false, error: msg };
          }
        }}
        eglises={eglises}
        prefillEvenement={quetePrefillEvent}
      />
    </div>
  );
}

/** Bloc date stylisé (jour + mois). */
function DateBlock({ iso }) {
  if (!iso) {
    return (
      <div style={{
        background: colors.card, color: colors.textMuted, borderRadius: "10px",
        width: 64, minWidth: 64, textAlign: "center", padding: "12px 4px",
      }}>—</div>
    );
  }
  const d = new Date(iso);
  if (isNaN(d)) {
    return null;
  }
  const day = d.getDate();
  const month = d.toLocaleDateString("fr-FR", { month: "short" }).toUpperCase().replace(".", "");
  return (
    <div
      style={{
        background: goldGradient,
        color: colors.bg,
        borderRadius: "10px",
        width: 64,
        minWidth: 64,
        textAlign: "center",
        padding: "12px 4px",
        boxShadow: "0 4px 16px rgba(228,167,44,0.40)",
      }}
    >
      <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em" }}>{month}</div>
      <div style={{ fontFamily: fonts.heading, fontSize: "30px", fontWeight: 700, lineHeight: 1 }}>{day}</div>
    </div>
  );
}

/** Section "Bénévoles" sur la carte d'un culte. */
function BenevolesSection({ summary, onManage }) {
  if (!summary || summary.total === 0) {
    return (
      <div style={{ marginTop: "8px" }}>
        <button
          onClick={onManage}
          style={{
            width: "100%",
            padding: "8px 12px",
            background: "transparent",
            border: `1px dashed ${colors.border}`,
            borderRadius: "8px",
            color: colors.textMuted,
            fontSize: "13px",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = colors.gold; e.currentTarget.style.borderColor = colors.goldBorder; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = colors.textMuted; e.currentTarget.style.borderColor = colors.border; }}
        >
          🤝 Assigner des bénévoles
        </button>
      </div>
    );
  }

  const pct = summary.total > 0 ? (summary.confirmes / summary.total) * 100 : 0;
  const couleur = pct === 100 ? "#2DD4A0" : pct >= 50 ? colors.gold : "#FB923C";

  return (
    <div style={{
      marginTop: "8px",
      padding: "8px 12px",
      background: `${couleur}15`,
      border: `1px solid ${couleur}40`,
      borderRadius: "8px",
      display: "flex", alignItems: "center", gap: "8px",
      fontSize: "13px",
    }}>
      <span style={{ fontSize: "16px" }}>🤝</span>
      <span style={{ color: colors.text }}>
        Bénévoles : <strong style={{ color: couleur }}>{summary.confirmes}/{summary.total} confirmés</strong>
      </span>
      <button
        onClick={onManage}
        style={{
          marginLeft: "auto",
          color: couleur,
          fontSize: "12px",
          fontWeight: 600,
          padding: "0 4px",
        }}
      >
        Gérer →
      </button>
    </div>
  );
}

/** Section "Quête" sur la carte d'un culte. */
function QueteSection({ evt, quete, onCreate }) {
  const isFutur = new Date(evt.date_debut) > new Date();

  if (quete) {
    const sym = { EUR: "€", XOF: "F CFA", XAF: "F CFA", CAD: "$", CHF: "CHF", USD: "$" }[quete.devise] || quete.devise;
    return (
      <div style={{
        marginTop: "10px", padding: "8px 12px",
        background: colors.goldDim,
        border: `1px solid ${colors.goldBorder}`,
        borderRadius: "8px",
        display: "flex", alignItems: "center", gap: "8px",
        fontSize: "13px",
      }}>
        <span style={{ fontSize: "16px" }}>💰</span>
        <span style={{ color: colors.text }}>
          Quête : <strong style={{ color: colors.gold }}>{(quete.total || 0).toLocaleString("fr-FR")} {sym}</strong>
        </span>
        <span style={{ color: colors.green, marginLeft: "auto" }}>✓</span>
      </div>
    );
  }

  if (isFutur) return null;

  return (
    <div style={{ marginTop: "10px" }}>
      <button
        onClick={onCreate}
        style={{
          width: "100%",
          padding: "8px 12px",
          background: "transparent",
          border: `1px dashed ${colors.goldBorder}`,
          borderRadius: "8px",
          color: colors.gold,
          fontSize: "13px",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          cursor: "pointer",
          transition: "all 150ms",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = colors.goldDim; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
      >
        💰 Enregistrer la quête
      </button>
    </div>
  );
}
